//import App from '@gral/app';
import moment from 'moment-timezone';

//import server from '@flux/actions/server';
import logger from '@gral/log';
import { fetchData, postData, putData, updateData } from '@redux/slices/functionals/request/requestActions';
import { CView, IReadingData, TElementID, CReading } from '@ts/ui-models';
import { GetLatestReadingsDto, GetReadingsDto, DATE_RANGE_MODE } from '@ts/ui-models';
import { IMapOptions } from '@interfaces/maps/Map.interface';
import { CMapElement } from '@models/maps/Map';

const log = logger(`MAP-Service`);

//#region MAPS
export const fetchView = async (dispatch, viewID: string): Promise<CView | null> => {
  let needs = {
    url: `/api/view/${viewID}`,
    data: {},
  };

  const reqView = (await dispatch(fetchData({ key: 'mapView', url: needs.url, dataBody: needs.data }))).payload?.data;
  if (reqView) {
    return new CView(reqView);
  }
  return null;
};
export const putPropertiesView = (dispatch, viewID: string, properties: IMapOptions) => {
  return dispatch(
    updateData({
      //key: 'mapProperties',
      url: `/api/view/${viewID}`,
      dataBody: {
        properties,
      },
    })
  );
};

export const fetchRulesetData = async (dispatch: any, queries: any[]) => {
  const results = await Promise.all(
    queries.map(query =>
      dispatch(
        fetchData({
          url: '/api/processRulesets',
          meta: query.data,
          withoutCancel: true,
        })
      )
    )
  );

  return results.map((res: any) => res.payload?.data);
};
//#endregion

export const getLatestPositionByElementIds = async (
  dispatch,
  elementIds: string[],
  view: CView
): Promise<Record<TElementID, IReadingData[]>> => {
  return new Promise(async (resolve, reject) => {
    if (elementIds && elementIds.length > 0) {
      const body: GetLatestReadingsDto = {
        elementIds: elementIds,
        readingTypeId: view.getReadingTypePosition(),
      };
      const request = await dispatch(
        postData({
          key: 'mapPositions',
          url: `/api/v3/maps/latestPositions`,
          dataBody: body,
        })
      );

      const readings = request?.payload?.data?.data;
      if (readings) {
        resolve(readings.readings);
      } else {
        reject('No positions found');
      }
    }
  });
};

export const getPositionByElementIds = async (
  dispatch,
  elementIds: string[],
  view: CView,
  numberOfPoints?: number
): Promise<Record<TElementID, IReadingData[]>> => {
  const { properties: mapOptions } = view;
  return new Promise(async (resolve, reject) => {
    const body: GetReadingsDto = {
      elementIds: elementIds,
      mode: mapOptions.mode as DATE_RANGE_MODE,
      readingTypeId: view.getReadingTypePosition(),
      dateRange: mapOptions.dateRange,
      lastSeconds: mapOptions.lastSeconds,
      ...(numberOfPoints && { numberOfPoints }),
      timezone: moment.tz.guess(),
    };
    const request = await dispatch(
      postData({
        key: 'mapPositions',
        url: `/api/v3/maps/positions`,
        dataBody: body,
      })
    );

    const readings = request?.payload?.data?.data;
    if (readings) {
      resolve(readings.readings);
    } else {
      reject('No positions found');
    }
  });
};

export const getBatchElementPositions = async (
  dispatch: any,
  itemIds: string[] | null,
  listItems: Record<TElementID, CMapElement>,
  view: CView,
  resetMap: boolean,
  isCancelled: () => boolean, // Callback para verificar cancelación
  numberOfPoints?: number,
) => {
  if (!itemIds) return;
  const batchSize = 25;
  let start = 0;
  const modifiedItemIDs = new Set<TElementID>();

  const processBatches = async (fetchFunction: (dispatch: any, batch: string[], view: CView, numberOfPoints?: number) => any) => {
    start = 0;

    while (start < itemIds.length) {
      if (isCancelled()) {
        log.info('Fetch cancelled, stopping batch processing.');
        break;
      }
      const batch = itemIds.slice(start, start + batchSize);
      let attempts = 0;

      while (attempts < 2) {
        try {
          const lastPositionByItemId = await fetchFunction(dispatch, batch, view, numberOfPoints);

          if (lastPositionByItemId) {
            Object.keys(lastPositionByItemId).forEach(itemID => {
              const item = listItems[itemID];
              if (item) {
                modifiedItemIDs.add(itemID);
                item.reset();
                const readingType = view.getReadingTypePosition();
                const formatReadings: CReading[] = lastPositionByItemId[itemID][readingType].map(
                  (reading: IReadingData) => item.unitFormatter && new CReading(readingType, reading, item.unitFormatter)
                );
                item.newReadings = { [view.getReadingTypePosition()]: formatReadings };
                item.notify();
              }
            });

            break;
          } else {
            attempts++;
            log.warn(`Batch ${start / batchSize + 1} returned undefined. Retrying...`);
          }
        } catch (error) {
          log.error(`Error fetching batch ${start / batchSize + 1}:`, error);
          attempts++;
        }
      }

      if (attempts === 2) {
        log.error(`Batch ${start / batchSize + 1} failed after 2 attempts. Skipping to next batch.`);
      }

      start += batchSize;
    }

    //Clean items no modified
    if (resetMap) {
      const unmodifiedItemIDs = Object.keys(listItems).filter(itemID => !modifiedItemIDs.has(itemID));
      unmodifiedItemIDs.forEach(itemID => {
        const item = listItems[itemID];
        if (item) {
          item.reset();
          item.notify();
        }
      });
    }
  };

  // obtener las posiciones actuales si `view.properties.now` es verdadero
  if (view.properties.mode === 'lastPosition' || !view.properties.mode) {
    await processBatches(getLatestPositionByElementIds);
  } else {
    //obtener posiciones históricas si `view.properties.now` es falso o después de la fase 1
    await processBatches(getPositionByElementIds);
  }
};

